import { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Sidebar from "./components/Sidebar";
import ProtectedRoute from "./routes/ProtectedRoute";
import { protectedRoutes, publicRoutes } from "./routes/routes";
import NotFoundPage from "./pages/NotFoundPage";
import { useAuth } from "./context/AuthContext";
import MobileHeader from "./components/MobileHeader";
import ComingSoon from "./pages/ComingSoon";
import { membershipAxios } from "./services/membership/MembershipAxios";
import Loader from "./components/common/Loader";
import ButtonWs from "./components/ButtonWs";
import { getSurveyValidateFormSurvey } from "./services/survey/surveyService";
import { getUserSettings } from "./services/settings/userSettings";
import { handleApi } from "./utils/handleApi";
import { decodeTokenService } from "./services/decodeTokenService";
import { Language } from "./utils/dictionary";
import { getGeoLocation } from "./services/settings/geoLocation";
import WelcomeModal from "./components/WelcomeModal";
import TutorialTooltip from "./components/TutorialTooltip";
import { useTutorialContext } from "./context/TutorialContext";
import { TutorialSetup } from "./components/TutorialSetup";
import { useWebsitesStore } from "./store/websitesStore";
import { PUBLIC_ROUTES, SKIP_ROUTES } from "./const/routesConst";
import { isResponsiveRoute } from "./helpers/routeHelper";
import { Country } from "./const/countryList";
import { useFirstPromoter } from "@/feature/firstPromoter/hooks/useFirstPromoter.hook";

const AppContent = ({ isTutorial }: { isTutorial: boolean }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const [isHovered] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  // const [isLoading, setIsLoading] = useState(false);

  useFirstPromoter(true);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  const verifyMembership = async () => {
    if (
      protectedRoutes.some((route) => location.pathname === route.path) ===
      false
    ) {
      return;
    }

    if (SKIP_ROUTES.includes(location.pathname)) {
      return;
    }

    try {
      // setIsLoading(true);
      const membership = await membershipAxios();
      if (location.pathname === "/plans" && membership.credits > 0) {
        navigate("/home");
      }
      if (location.pathname === "/onboarding" && membership.credits < 1) {
        navigate("/plans");
      }
      const redirectedToPreferences = await checkUserPreferences();
      if (redirectedToPreferences) return;

      await checkSurvey();
      // setIsLoading(false);
    } catch (error: any) {
      console.error("Error fetching membership:", error);
      if (error.response?.data.trigger === "NOT_ACTIVE_MEMBERSHIP") {
        navigate("/plans");
      }
      // setIsLoading(false);
    }
  };

  const checkUserPreferences = async (): Promise<boolean> => {
    const { error } = await handleApi(getUserSettings(), false);

    if (!error) return false;

    if (error.response?.status === 404) {
      navigate("/user/preferences");
      return true;
    }

    const errorData = error.response?.data as { message?: string } | undefined;
    toast.error(errorData?.message || "Error loading user settings");
    return false;
  };

  const checkSurvey = async () => {
    const userId = decodeTokenService.getUserIdFromToken();
    if (!userId) return;
    const survey = await getSurveyValidateFormSurvey("default", "user", userId);
    if (survey.data.status !== "success") {
      navigate("/survey");
    }
  };

  useEffect(() => {
    if (isLoading) return;
    verifyMembership();
  }, [location.pathname, isLoading]);

  useEffect(() => {
    if (isLoading) return;
    if (
      isAuthenticated &&
      (location.pathname === "/login" || location.pathname === "/signup")
    ) {
      navigate("/home");
    }
  }, [isAuthenticated, location.pathname, navigate, isLoading]);

  return (
    // <SubscriptionProvider>
    <div
      className={`hard-flex min-h-screen bg-[#F5F6FA] font-['Plus_Jakarta_Sans'] ${
        location.pathname === "/" ? "p-0" : ""
      }`}
    >
      {isAuthenticated && !PUBLIC_ROUTES.includes(location.pathname) && (
        <MobileHeader />
      )}
      {/* Sidebar for Desktop */}
      {isAuthenticated && !PUBLIC_ROUTES.includes(location.pathname) && (
        <div className="hidden md:block">
          <Sidebar isTutorial={isTutorial} />
        </div>
      )}

      {/* Main Content */}
      <div
        className={`flex-1 ${
          isAuthenticated &&
          !PUBLIC_ROUTES.includes(location.pathname) &&
          location.pathname !== "/"
            ? location.pathname === "/website/builder"
              ? isHovered
                ? "md:ml-60 ml-0"
                : "md:ml-16 ml-0"
              : "md:ml-60 ml-0"
            : "ml-0"
        } ${
          isAuthenticated &&
          !PUBLIC_ROUTES.includes(location.pathname) &&
          isResponsiveRoute(location.pathname)
            ? "pt-24 pb-8 md:pt-0 md:pb-0"
            : ""
        }`}
      >
        <Routes>
          {/* Public Routes */}
          {publicRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
          {isLoading ? (
            <Route
              path="*"
              element={
                <div className="flex justify-center items-center h-screen">
                  <Loader />
                </div>
              }
            />
          ) : (
            protectedRoutes.map(({ path, element }) => (
              <Route
                key={path}
                path={path}
                element={<ProtectedRoute>{element}</ProtectedRoute>}
              />
            ))
          )}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </div>
    // </SubscriptionProvider>
  );
};

const TUTORIAL_HOME_ROUTE = "/home";
const TUTORIAL_ALLOWED_ROUTES = ["/home", "/onboarding"] as const;

const AppShell = () => {
  const location = useLocation();
  const { websites, isLoading, initialize } = useWebsitesStore();
  const [hasInitializedWebsites, setHasInitializedWebsites] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  useEffect(() => {
    if (location.pathname !== TUTORIAL_HOME_ROUTE) return;
    if (hasInitializedWebsites) return;
    void (async () => {
      initialize();
      setHasInitializedWebsites(true);
    })();
  }, [hasInitializedWebsites, initialize, location.pathname]);

  const { setLanguage, setCountry } = useAuth();

  const languageMap: Record<string, Language> = {
    es: Language.SPANISH,
    en: Language.ENGLISH,
    pt: Language.PORTUGUESE,
  };

  const getUserPreferences = async () => {
    const { res, error } = await handleApi(getUserSettings(), false);

    // if (error && error.response?.status === 404) {
    //   try {
    //     const geoLocation = await getGeoLocation();
    //     const geoLanguage = geoLocation?.language;
    //     if (geoLanguage && languageMap[geoLanguage]) {
    //       setLanguage(languageMap[geoLanguage]);
    //     } else {
    //       // setLanguage(Language.SPANISH);
    //     }
    //   } catch (geoError) {
    //     console.error("Error getting geo location:", geoError);
    //     // setLanguage(Language.SPANISH);
    //   }
    //   return;
    // }

    // if (error) {
    //   // setLanguage(Language.SPANISH);
    //   return;
    // }

    if (res?.language && res?.country) {
      const language = languageMap[res.language];
      if (language) {
        setLanguage(language);
        setCountry(res.country as Country);
      } 
      // else {
      //   // setLanguage(Language.SPANISH);
      //   setCountry(Country.COLOMBIA);
      // }
    }
  };

  useEffect(() => {
    getUserPreferences();
  }, []);

  const {
    isWelcomeOpen,
    isTutorialActive,
    currentStep,
    pauseTutorial,
    startTutorial,
    nextStep,
    closeTutorial,
    restartTutorial,
  } = useTutorialContext();

  useEffect(() => {
    if (!isTutorialActive) return;
    const isAllowedRoute = (TUTORIAL_ALLOWED_ROUTES as readonly string[]).includes(
      location.pathname
    );
    if (!isAllowedRoute) {
      restartTutorial();
    }
  }, [isTutorialActive, location.pathname, restartTutorial]);

  const tutorialEligible =
    location.pathname === TUTORIAL_HOME_ROUTE &&
    hasInitializedWebsites &&
    websites.length === 0 &&
    !isLoading &&
    window.innerWidth >= 768;

  useEffect(() => {
    if (!tutorialEligible) {
      setShowWelcomeModal(false);
      return;
    }
    const timeoutId = window.setTimeout(() => setShowWelcomeModal(true), 350);
    return () => window.clearTimeout(timeoutId);
  }, [tutorialEligible]);

  return (
    <>
      {showWelcomeModal && (
        <WelcomeModal
          isOpen={isWelcomeOpen}
          onStart={startTutorial}
          onClose={closeTutorial}
        />
      )}

      <TutorialTooltip
        step={currentStep}
        onNext={nextStep}
        onClose={closeTutorial}
        isActive={isTutorialActive}
        onPause={pauseTutorial}
      />

      {/* <InitialSurvey /> */}
      {!isResponsiveRoute(location.pathname) ? (
        <>
          <div className="hidden md:block">
            <AppContent isTutorial={tutorialEligible} />
            <ButtonWs />
          </div>
          <div className="md:hidden">
            <ComingSoon />
          </div>
        </>
      ) : (
        <AppContent isTutorial={tutorialEligible} />
      )}
    </>
  );
};

const App = () => {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Toaster
        position="top-right"
        containerStyle={{
          zIndex: 99999,
        }}
        toastOptions={{
          duration: 2000,
        }}
      />
      <TutorialSetup />
      <AppShell />
    </Router>
  );
};

export default App;
